import { useEffect, useState } from 'react';
import { api } from './api';
import { useAuth } from './auth';
import type { FamilyLanguage } from './languages';

/**
 * Words in the family's own language.
 *
 * Every Yorùbá word in the interface used to be written into the component:
 * the greeting, each page's subtitle, the names of the story types. A family
 * who chose Igbo still saw "Ẹ kú alẹ́" and "Àpótí Ìtàn" — which is worse than
 * plain English, because it says their language is listed but not present.
 *
 * Terms now come from the database, per language, and only once a native
 * speaker has approved them. Where there is no approved term, `term()` returns
 * undefined and the interface shows English alone. Nobody is ever shown
 * another people's language standing in for their own.
 */

export type Terms = Record<string, Record<string, string>>;

interface FamilyLanguagesResponse {
  languages: FamilyLanguage[];
  terms?: Terms;
}

let cache: Promise<FamilyLanguagesResponse> | null = null;

/** Called on sign-out and whenever the family changes its languages. */
export function resetTerms(): void {
  cache = null;
}

const FAMILY_ROLES = new Set(['parent', 'grandparent', 'teen', 'child']);

export function useFamilyTerms(): { term: (key: string) => string | undefined; primary: FamilyLanguage | null; loading: boolean } {
  const { user } = useAuth();
  const [data, setData] = useState<FamilyLanguagesResponse | null>(null);
  const [failed, setFailed] = useState(false);
  // Tutors and safeguarding staff have no family, so there is nothing to ask for.
  const inFamily = !!user && FAMILY_ROLES.has(user.role);

  useEffect(() => {
    if (!inFamily) {
      setFailed(true);
      return;
    }
    let alive = true;
    cache ??= api<FamilyLanguagesResponse>('/family/languages');
    cache
      .then((d) => alive && setData(d))
      .catch(() => {
        cache = null;
        // The interface is perfectly usable in English; never block on this.
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [inFamily]);

  const primary = data?.languages.find((l) => l.is_primary) ?? data?.languages[0] ?? null;

  return {
    primary,
    loading: !data && !failed,
    term: (key: string) => (primary ? data?.terms?.[primary.code]?.[key] : undefined),
  };
}

const GREETING_KEYS = ['greeting.morning', 'greeting.afternoon', 'greeting.evening', 'greeting.night'] as const;

/** Which greeting the hour calls for. The word itself comes from the family's language. */
export function greetingKey(now = new Date()): (typeof GREETING_KEYS)[number] {
  const h = now.getHours();
  if (h < 12) return 'greeting.morning';
  if (h < 16) return 'greeting.afternoon';
  if (h < 20) return 'greeting.evening';
  return 'greeting.night';
}

/** The English fallback, used only when the family's language has no approved greeting yet. */
export function greetingEnglish(now = new Date()): string {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 16) return 'Good afternoon';
  if (h < 20) return 'Good evening';
  return 'Good evening';
}
